import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import EmptyState from '../components/EmptyState';
import { useNavigate, useLocation } from '../routing';
import { creditsApi, formatCurrencyWhole } from '../utils/api';
import { track, EVENTS } from '../utils/analytics';
import { toast } from '../components/Toast';
import CreditsFilterBar from '../components/CreditsFilterBar';
import CreditsViewTabs, { consumeViewEntry } from '../components/CreditsViewTabs';
import CreditsTable from '../components/CreditsTable';
import CreditsMetricsRow from '../components/CreditsMetricsRow';
import { useSettings } from '../context/SettingsContext';
import { useAuth } from '../context/AuthContext';
import { SORT_ACCESSORS, buildCreditGroups, readSavedCreditsFilters, writeSavedCreditsFilters, initialCreditsRange, creditsDefaultRange, CREDITS_DEFAULT_PRESET, CREDIT_STATUS } from '../utils/creditsShared';
import { vatMultiplier } from '../utils/money';
import { useIsMobile } from '../utils/useIsMobile';
import { periodLabel, PRESETS_ANALYTIC } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import Icon from '../components/Icon';
import ConfirmModal from '../components/ConfirmModal';
import CreditReceiveModal from '../components/CreditReceiveModal';
import CreditCloseReasonPicker from '../components/CreditCloseReasonPicker';
import { CREDIT_CLOSE_REASONS } from '../utils/creditCloseReasons';
import CreditDetailSheet from '../components/CreditDetailSheet';
import InlineLoadError from '../components/InlineLoadError';
import MobileSort from '../components/MobileSort';
import { useSortable } from '../utils/useSortable';
import PageViewAsMenu from '../components/ViewAsMenu';
import HqOnlyNotice from '../components/HqOnlyNotice';

// /credits/history — credits already resolved, status permanently locked
// (server-side) to 'התקבל' + 'בוטל'. This is the "reward" view: how much
// came back this period, front and center, on a page where the number can't
// disagree with anything below it (see CreditsOpenPage for the other half of
// CreditsViewTabs and the fuller rationale for the route split).
const HISTORY_STATUSES = [CREDIT_STATUS.RECEIVED, CREDIT_STATUS.CANCELLED];

export default function CreditsHistoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { vatRate } = useSettings();
  const { canEdit, user, restaurantId } = useAuth();
  // Restore the sticky supplier filter saved this session, shared with
  // /credits via the same sessionStorage key. The period comes back only when
  // returning to this view (initialCreditsRange).
  const saved = useMemo(() => readSavedCreditsFilters(user?.id, restaurantId), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [credits,      setCredits]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(false);
  const [nameFilter,   setNameFilter]   = useState(saved.nameFilter ?? '');
  // Always opens on the default — see initialCreditsRange for the two exceptions.
  const initialRange = useMemo(() => initialCreditsRange(saved, location), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [dateFrom,     setDateFrom]     = useState(initialRange.from);
  const [dateTo,       setDateTo]       = useState(initialRange.to);
  // Rolls a preset forward when the tab comes back after the day changed, and
  // is the key the period is saved under (utils/usePresetPeriod.js).
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_ANALYTIC, (f, t) => { setDateFrom(f); setDateTo(t); });
  const [creditSheet,  setCreditSheet]  = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  // The close-reason chips shown INSIDE a `needsReason` confirm dialog. Held on
  // the page rather than inside confirmState so picking a chip doesn't rebuild
  // the dialog object (and with it the onConfirm closure) on every tap.
  const [closeReason,  setCloseReason]  = useState('');
  const [closeNote,    setCloseNote]    = useState('');
  // Every confirm opens with the chips blank — carrying the previous credit's
  // answer over would be the app pre-filling an audit field on the user's behalf.
  const openConfirm = (state) => { setCloseReason(''); setCloseNote(''); setConfirmState(state); };
  const [modal,        setModal]        = useState(null);
  const isFilterMobile = useIsMobile();

  // Token guard against out-of-order responses. Mutations call load()
  // imperatively (not from an effect, so there is no cleanup to lean on),
  // and two quick actions — cancel one credit, immediately receive
  // another — put two loads in flight. Without this, the SLOWER one wins
  // whenever it lands last and repaints the table with pre-mutation rows.
  const loadToken = useRef(0);
  const load = useCallback(async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await creditsApi.getAll({
        status: HISTORY_STATUSES.join(','),
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      if (token !== loadToken.current) return;   // superseded
      setCredits(r.data);
    } catch {
      // Without this the rejection escapes (unhandled) AND the render
      // falls through to EmptyState — telling the user they have no
      // credits when the truth is the request failed.
      if (token !== loadToken.current) return;
      setLoadError(true);
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // CreditsMetricsRow owns its own request, so `load()` alone refreshes the
  // table while leaving the cards on pre-mutation totals. EVERY mutation must
  // go through this instead — bumping the key is what re-runs the cards'
  // fetch. Two paths deliberately call `load()` bare instead: mount (the
  // metrics row fetches on its own first render, so bumping would duplicate
  // it) and the table's retry button (a failed load is not a mutation, and
  // the metrics row carries its own independent retry).
  const [metricsKey, setMetricsKey] = useState(0);
  const reloadAll = useCallback(() => {
    load();
    setMetricsKey(k => k + 1);
  }, [load]);

  useEffect(() => {
    track(EVENTS.CREDIT_VIEW_SHOWN, { view: 'history', entry: consumeViewEntry() });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    writeSavedCreditsFilters(user.id, restaurantId, { locKey: location.key, nameFilter, dateFrom, dateTo, presetKey });
  }, [user?.id, restaurantId, location.key, nameFilter, dateFrom, dateTo, presetKey]);

  const clearFilters = () => {
    const fresh = creditsDefaultRange();
    setNameFilter('');
    setDateFrom(fresh.from);
    setDateTo(fresh.to);
  };

  const openReceiveModal = async (credit) => {
    const r = await creditsApi.getCandidateDocs(credit.supplier_id);
    setModal({ type: 'receive', credit, candidateDocs: r.data, selectedDocId: '', reason: '', note: '' });
  };

  // Closing a credit is a money-moving write (it restores the item and
  // recomputes the source document's totals), so the server records WHO — and,
  // when no credit document is linked, demands a reason. The modal already
  // blocks confirming without one; these fields are simply passed along, and are
  // ignored server-side once a document is picked.
  const handleReceive = async () => {
    try {
      await creditsApi.update(modal.credit.id, {
        status: CREDIT_STATUS.RECEIVED,
        linked_credit_doc_id: modal.selectedDocId || null,
        closed_reason: modal.selectedDocId ? null : (modal.reason || null),
        closed_note:   modal.selectedDocId ? null : (modal.note || null),
      });
      track(EVENTS.CREDIT_STATUS_CHANGED, {
        to_status: CREDIT_STATUS.RECEIVED,
        linked_to_doc: !!modal.selectedDocId,
        close_reason: modal.selectedDocId ? null : (modal.reason || null),
      });
      toast('הזיכוי סומן כהתקבל');
      setModal(null);
      reloadAll();
    } catch { toast('שגיאה', 'error'); }
  };

  const creditDetail = (c) =>
    `${c.supplier_name} · ${c.item_description || '—'} · ${formatCurrencyWhole(c.credit_total)} (ללא מע״מ)`;

  // Cancelling is its own prompt, not a generic status flip, and it is worded
  // identically to the open page's: the same "בטל זיכוי" button must not ask
  // the question two different ways depending on which tab you happen to be
  // on. (It briefly did — the tab split left one page routing this through
  // handleStatusChange, which says "לשנות את הסטטוס ל-בוטל" instead.)
  //
  // Cancelling CLOSES the credit and never carries a credit document, so it
  // always needs a reason — `needsReason` puts the chips in the dialog and keeps
  // its button unusable until one is picked.
  const handleCancel = (credit) => openConfirm({
    title: 'ביטול זיכוי',
    message: `לבטל את הזיכוי על "${credit.item_description}"? הזיכוי יפסיק להיספר בסכומים.`,
    detail: creditDetail(credit),
    tone: 'danger',
    confirmLabel: 'בטל זיכוי',
    needsReason: true,
    reasons: CREDIT_CLOSE_REASONS.filter(r => r.key !== 'received_no_doc'),
    reasonLabel: 'למה מבטלים? (חובה)',
    onConfirm: async ({ reason, note }) => {
      setConfirmState(null);
      try {
        await creditsApi.update(credit.id, {
          status: CREDIT_STATUS.CANCELLED, closed_reason: reason, closed_note: note || null,
        });
        track(EVENTS.CREDIT_STATUS_CHANGED, {
          to_status: CREDIT_STATUS.CANCELLED, linked_to_doc: false, close_reason: reason,
        });
        toast('הזיכוי בוטל');
        reloadAll();
      } catch { toast('שגיאה', 'error'); }
    },
  });

  // Direct status flip for the transitions that genuinely are one: התקבל→פתוח
  // and בוטל→פתוח. בוטל→התקבל goes through openReceiveModal so the user picks
  // the linked credit doc, and →בוטל goes through handleCancel above.
  const handleStatusChange = (credit, newStatus) => openConfirm({
    title: `שינוי סטטוס ל"${newStatus}"`,
    message: `לשנות את הסטטוס של הזיכוי ל-"${newStatus}"?`,
    detail: creditDetail(credit),
    tone: newStatus === CREDIT_STATUS.CANCELLED ? 'danger' : 'success',
    confirmLabel: `שנה ל"${newStatus}"`,
    // No `needsReason`: every caller here passes 'פתוח', which REOPENS the
    // credit. Nothing is being settled, so there is nothing to explain — and
    // the server clears the close audit columns on this transition.
    onConfirm: async () => {
      setConfirmState(null);
      try {
        await creditsApi.update(credit.id, { status: newStatus });
        track(EVENTS.CREDIT_STATUS_CHANGED, { to_status: newStatus, linked_to_doc: false });
        toast(`הסטטוס שונה ל-${newStatus}`);
        reloadAll();
      } catch { toast('שגיאה', 'error'); }
    },
  });

  // Status is already server-locked to received+cancelled — only the name
  // search narrows the rows further, client-side.
  const filtered = useMemo(
    () => credits.filter(c => !nameFilter || c.supplier_name.includes(nameFilter)),
    [credits, nameFilter],
  );
  // By preset key, so a default rolled forward by usePresetPeriod stays default.
  const isDefaultDate = presetKey === CREDITS_DEFAULT_PRESET;
  const hasFilters = Boolean(nameFilter) || !isDefaultDate;
  const vatMult = vatMultiplier(vatRate);
  // The always-visible summary — open/received/cancelled money + open
  // count — moved to CreditsMetricsRow, independent of this page's own
  // status-locked table.
  const metricsRow = <CreditsMetricsRow dateFrom={dateFrom} dateTo={dateTo} nameFilter={nameFilter} refreshKey={metricsKey} />;

  const { sorted, sort, setSortTo, indicator: sortInd, headerProps: sortableTh } =
    useSortable(filtered, SORT_ACCESSORS);
  const groups = useMemo(() => buildCreditGroups(sorted, vatMult), [sorted, vatMult]);

  // Row menu items differ by which of the two history statuses the row is in.
  // בוטל→התקבל still routes through openReceiveModal (linked-doc picker);
  // every other transition here is a direct flip via handleStatusChange.
  const renderRowActions = (c, closeMenu, menuBtn) => (
    c.status === CREDIT_STATUS.RECEIVED ? (
      <>
        <button className="row-menu-item" style={menuBtn}
          onClick={() => { closeMenu(); handleStatusChange(c, CREDIT_STATUS.OPEN); }}>
          <Icon name="undo" size={16} strokeWidth={1.6} /> החזר לפתוח
        </button>
        <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--danger-ink)' }}
          onClick={() => { closeMenu(); handleCancel(c); }}>
          <Icon name="close" size={16} strokeWidth={1.6} /> בטל זיכוי
        </button>
      </>
    ) : (
      <>
        <button className="row-menu-item" style={menuBtn}
          onClick={() => { closeMenu(); handleStatusChange(c, CREDIT_STATUS.OPEN); }}>
          <Icon name="undo" size={16} strokeWidth={1.6} /> החזר לפתוח
        </button>
        <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--success-ink)', fontWeight: 600 }}
          onClick={() => { closeMenu(); openReceiveModal(c); }}>
          <Icon name="check" size={16} strokeWidth={1.6} /> סמן כהתקבל
        </button>
      </>
    )
  );

  return (
    <div>
      <div className="page-title-row">
        <div className="page-title-group">
          <h1 className="page-title">זיכויים</h1>
          <HqOnlyNotice money={formatCurrencyWhole} amount={loadError ? null : filtered.filter(c => c.hq_only && c.status === CREDIT_STATUS.RECEIVED).reduce((n, c) => n + (c.credit_total || 0), 0)} pending={loading} basis="זיכויים שהתקבלו בתקופה (ללא מע״מ)" />
        </div>
        <div className="page-title-actions">
          <button className="btn btn-sm btn-excel"
            onClick={() => creditsApi.exportExcel({
              status: HISTORY_STATUSES.join(','),
              date_from: dateFrom || undefined,
              date_to: dateTo || undefined,
              name: nameFilter || undefined,
            })}
            title="ייצוא לאקסל">
            <Icon name="spreadsheet" size={14} />
            ייצוא
          </button>
          <PageViewAsMenu />
        </div>
      </div>

      <CreditsFilterBar
        nameFilter={nameFilter} setNameFilter={setNameFilter}
        dateFrom={dateFrom}     dateTo={dateTo}
        setDateRange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        onClearAll={clearFilters}
        metricsRow={isFilterMobile ? metricsRow : null}
        sortControl={
          <MobileSort
            options={[
              { key: 'date',     label: 'תאריך מסמך' },
              { key: 'total',    label: 'סכום זיכוי' },
              { key: 'supplier', label: 'שם ספק' },
            ]}
            sort={sort}
            onChange={setSortTo}
          />
        }
      />

      {!isFilterMobile && metricsRow}

      <div className="card">
        {/* The card's own header row, on EVERY breakpoint — same placement as
            /suppliers. It used to ride the filter bar on mobile (as a segmented
            pill) and sit here on desktop, which meant the control changed both
            shape and position at 720px; the shared CSS now renders one slim
            underline bar at both sizes, so there is nothing left to switch. */}
        <CreditsViewTabs active="history" />

        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את הזיכויים" onRetry={load} />
        ) : filtered.length === 0 ? (
          <EmptyState compact={hasFilters} message={hasFilters ? `לא נמצאו זיכויים ב${periodLabel(dateFrom, dateTo)} לפי הסינון שנבחר` : 'אין היסטוריית זיכויים'}>
            {/* The cards above are period-wide and ignore this tab's status
                lock, so they can show this supplier's money while the table
                below is empty — the rows are on the other tab. Say so, and
                offer the trip; nameFilter persists across both routes. */}
            {nameFilter && (
              <button className="empty-action" onClick={() => navigate('/credits', { state: { keepPeriodFrom: location.key } })}>
                חפש את "{nameFilter}" בזיכויים הפתוחים
              </button>
            )}
          </EmptyState>
        ) : (
          <CreditsTable
            groups={groups}
            canEdit={canEdit}
            selectMode={false}
            renderRowActions={renderRowActions}
            vatMult={vatMult}
            sortableTh={sortableTh}
            sortInd={sortInd}
            onOpenDocument={(id) => navigate(`/document/${id}`)}
            onRowClick={setCreditSheet}
          />
        )}
      </div>

      {/* Mobile credit detail sheet. Status IS shown here — this view mixes
          התקבל and בוטל, so it's the one thing distinguishing the rows. */}
      {creditSheet && (
        <CreditDetailSheet
          credit={creditSheet}
          onClose={() => setCreditSheet(null)}
          vatMult={vatMult}
          showStatus
          renderActions={(c, closeThen) => (
            <>
              <button className="btn btn-secondary" onClick={closeThen(() => navigate(`/document/${c.document_id}`))}>
                <Icon name="eye" size={16} /> צפה במסמך
              </button>
              {canEdit && (
                <button className="btn btn-secondary" onClick={closeThen(() => handleStatusChange(c, CREDIT_STATUS.OPEN))}>
                  <Icon name="undo" size={16} /> החזר לפתוח
                </button>
              )}
              {canEdit && c.status === CREDIT_STATUS.RECEIVED && (
                <button className="btn btn-danger" onClick={closeThen(() => handleCancel(c))}>
                  <Icon name="close" size={16} /> בטל זיכוי
                </button>
              )}
              {canEdit && c.status === CREDIT_STATUS.CANCELLED && (
                <button className="btn btn-primary" onClick={closeThen(() => openReceiveModal(c))}>
                  <Icon name="check" size={16} /> סמן כהתקבל
                </button>
              )}
            </>
          )}
        />
      )}

      {confirmState && (
        <ConfirmModal
          {...confirmState}
          // After the spread, so they win over the raw values in confirmState:
          // onConfirm is handed the chip answer, and the button stays unusable
          // until there is one (the server would reject it with a 400, and a
          // dialog that lets you press אישור and then fails teaches distrust).
          confirmDisabled={Boolean(confirmState.needsReason) && !closeReason}
          onConfirm={() => confirmState.onConfirm({ reason: closeReason, note: closeNote })}
          onCancel={() => setConfirmState(null)}
        >
          {confirmState.needsReason && (
            <CreditCloseReasonPicker
              value={closeReason} note={closeNote}
              onChange={setCloseReason} onNoteChange={setCloseNote}
              // Cancelling drops 'התקבל זיכוי בפועל': a credit that WAS received
              // is marked התקבל, never cancelled, so offering it here only
              // invites a tap that records the opposite of what happened.
              reasons={confirmState.reasons || CREDIT_CLOSE_REASONS}
              label={confirmState.reasonLabel || undefined}
            />
          )}
        </ConfirmModal>
      )}

      {modal?.type === 'receive' && (
        <CreditReceiveModal
          modal={modal}
          onSelectDoc={(id) => setModal(m => ({ ...m, selectedDocId: id }))}
          onSetReason={(key) => setModal(m => ({ ...m, reason: key }))}
          onSetNote={(text) => setModal(m => ({ ...m, note: text }))}
          onConfirm={handleReceive}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
